import { useState } from "react";
import toast from "react-hot-toast";
import { X, User, Mail, Phone, Check } from "lucide-react";

export const BookingFormPopup = ({ bookingData, onClose }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!name.trim()) {
      newErrors.name = "Full name is required";
    } else if (name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/.test(phone)) {
      newErrors.phone = "Please enter a valid phone number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      const response = await fetch(
        "https://sastha.azerotech.com/api/sendBookingmail.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...bookingData,  // send everything from Home
            name,
            email,
            phone,
          }),
        }
      );

      const data = await response.json();

      if (data.status === "success") {
        toast.success("Booking confirmed! Emails sent successfully.");
        onClose();
      } else {
        toast.error(data.message || "Failed to send booking emails.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong.");
    }
  };

  const formatLabel = (key) => {
  const labels = {
    bookingType: "Booking Type",
    roomType: "Room Type",
    checkIn: "Check In",
    checkOut: "Check Out",
    rooms: "Rooms",
    guests: "Guests",
    eventName: "Event Name",
    eventDate: "Event Date",
    eventTime: "Event Time",
    foodType: "Food Type",
  };

  return labels[key] || key;
};



  return (
    <div className="fixed inset-0 z-[999] flex items-end md:items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      
      <div className="relative w-full max-w-sm rounded-t-xl md:rounded-xl bg-white shadow-xl p-5 z-10 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <p className="text-[14px] sm:text-[15px] font-semibold text-[#1F2226]">
            Confirm Booking
          </p>
          
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 grid place-items-center rounded-full hover:bg-black/5"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Booking Details */}
        <div className="mb-6">
          <p className="text-[12px] text-[#9AA3AE] mb-3">Booking Details</p>
          <div className="border border-black/10 rounded-xl p-4 space-y-3">
            {Object.entries(bookingData).map(([key, value]) => {
              if(key==="bookingType") return;
              return(
              <div key={key} className="flex items-center justify-between">
                <p className="text-[14px] text-[#1F2226]">{formatLabel(key)}</p>
                <p className="text-[14px] font-semibold text-[#1F2226] capitalize">{value}</p>
              </div>
            )})}
          </div>
        </div>

        {/* User Info Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-3">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="w-4 h-4 text-[#9AA3AE]" />
              </div>
              <input
                type="text"
                placeholder="Full Name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (errors.name) setErrors({ ...errors, name: '' });
                }}
                className={`w-full pl-10 pr-4 py-3 border rounded-xl transition ${
                  errors.name ? 'border-red-500' : 'border-black/10'
                }`}
              />
              {errors.name && (
                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
              )}
            </div>

            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="w-4 h-4 text-[#9AA3AE]" />
              </div>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) setErrors({ ...errors, email: '' });
                }}
                className={`w-full pl-10 pr-4 py-3 border rounded-xl transition ${
                  errors.email ? 'border-red-500' : 'border-black/10'
                }`}
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>

            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Phone className="w-4 h-4 text-[#9AA3AE]" />
              </div>
              <input
                type="tel"
                placeholder="Phone"
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value);
                  if (errors.phone) setErrors({ ...errors, phone: '' });
                }}
                className={`w-full pl-10 pr-4 py-3 border rounded-xl transition ${
                  errors.phone ? 'border-red-500' : 'border-black/10'
                }`}
              />
              {errors.phone && (
                <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
              )}
            </div>
          </div>
        </form>

        {/* Buttons */}
        <div className="mt-6 sm:mt-8 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 rounded-xl
              bg-white border border-black/10 text-[#1F2226]
              text-[14px] sm:text-[15px]
              font-semibold hover:bg-black/5 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="flex-1 py-3 rounded-xl
              bg-primary text-white
              text-[14px] sm:text-[15px]
              font-semibold hover:opacity-90 transition"
          >
            <div className="flex items-center justify-center gap-2">
              Confirm
              <Check className="w-4 h-4" />
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};
